import {drawer, DrawerBody, DrawerFooter, DrawerHeader} from '@cypro/ui/components'
import {DeleteResourceButton} from '@cypro/ui/components/ui/delete-resource-button'
import {DataList, DataListItem} from '@cypro/ui/components/ui/data-list'
import {DataSection} from '@cypro/ui/components/ui/data-section'
import {Drawer, Badge, Box, Heading, Tabs} from '@chakra-ui/react'
import {useCan} from '@/shared/hooks/use-permissions'
import {useTranslate} from '@tolgee/react'
import {Patient, PatientGfr} from '@cypro/engine/schema'
import {GfrMethod, type GfrMethodType} from '@cypro/engine/database/schema'
import {formatGfr} from '@/modules/patients/functions'
import {useMemo} from 'react'
import {GFRAlert} from './gfr-alert'
import {formatDateKey, getDatetimeFormatter, multipleDigitsNumberFormatter} from '@cypro/ui/utils'
import {LuContactRound} from 'react-icons/lu'
import {ResourceDrawerSummary} from '@/shared/components/ui/resource-drawer-summary'
import {GfrDrawerTabsList} from './gfr-drawer-tabs-list'
import {ResourceDrawerActivitiesSection} from '@/shared/components/ui/resource-drawer-sections'
import {useGetPatientGfrActivities} from '@/modules/patients/api/use-get-patient-gfr-activities'
import {useGetPatientLatestGfrLab} from '@/modules/patients/api/use-get-patient-latest-gfr-lab'
import {useConfirmDeletePatientGfr} from '@/modules/patients/api/use-delete-patient-gfr'
import {getGfrDrawerId} from './gfr-drawer-util'

interface Props {
  gfr: PatientGfr
  patient: Patient
}

export function GfrDrawer({gfr, patient}: Props) {
  const {t} = useTranslate('common')
  const canEditPatient = useCan('patients.edit')
  const [activities] = useGetPatientGfrActivities(gfr.id)
  const [inUseGfr] = useGetPatientLatestGfrLab(gfr.patientId)
  const confirmDeleteGfr = useConfirmDeletePatientGfr(patient)

  async function onDelete() {
    if (await confirmDeleteGfr(gfr)) void drawer.close(getGfrDrawerId(gfr.id))
  }

  const items = useMemo(() => {
    const serumCreatinine: DataListItem = {
      label: t('gfr_table.serum'),
      value: gfr.serumCreatinine ? `${gfr.serumCreatinine} ${t(`serum_unit.${gfr.serumUnit}`)}` : '-',
    }

    // only the values the method was calculated from, then the patient data at the sample (CY-102)
    const enteredValues: Record<GfrMethodType, DataListItem[]> = {
      [GfrMethod.enum.urinary_24h]: [
        {label: t('gfr.urine_creatinine'), value: gfr.urineCreatinine ? String(gfr.urineCreatinine) : '-'},
        {label: t('gfr.urine_volume'), value: gfr.urineVolume ? String(gfr.urineVolume) : '-'},
        {label: t('gfr.collection_time'), value: gfr.collectionTime ? String(gfr.collectionTime) : '-'},
        serumCreatinine,
      ],
      [GfrMethod.enum.cockcroft_gault]: [serumCreatinine],
      [GfrMethod.enum.jelliffe]: [serumCreatinine],
      [GfrMethod.enum.manual_entry]: [{label: t('gfr_value'), value: `${formatGfr(gfr.gfr)} ${t('ml_min')}`}],
    }

    const value: DataListItem[] = [
      ...(enteredValues[gfr.method] ?? []),
      {
        label: t('gfr.age_at_sample'),
        value:
          gfr.ageMonths != null
            ? t('gfr.age_value', {years: Math.floor(gfr.ageMonths / 12), months: gfr.ageMonths % 12})
            : '-',
      },
      {
        label: t('gfr.weight_at_sample'),
        value: gfr.weight ? multipleDigitsNumberFormatter.format(gfr.weight) : '-',
      },
      {
        label: t('gfr.height_at_sample'),
        value: gfr.height ? multipleDigitsNumberFormatter.format(gfr.height) : '-',
      },
      ...(gfr.method === GfrMethod.enum.jelliffe
        ? [
            {
              label: t('gfr.bsa_at_sample', 'BSA at sample'),
              value: gfr.bsa ? multipleDigitsNumberFormatter.format(gfr.bsa) : '-',
            },
          ]
        : []),
    ]

    return value
  }, [t, gfr])

  return (
    <>
      <DrawerHeader>
        <Drawer.Title>{t('gfr.calculation_audit')}</Drawer.Title>
      </DrawerHeader>

      <DrawerBody>
        <ResourceDrawerSummary
          title={`${patient.givenName} ${patient.familyName}`}
          icon={LuContactRound}
          description={gfr.createdAt ? t('added_on', {value: getDatetimeFormatter().format(gfr.createdAt)}) : '-'}
        />
        <Tabs.Root defaultValue="about" spaceY="5">
          <GfrDrawerTabsList />

          <Tabs.Content value="about" spaceY="8" display="flex" flexDirection="column">
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
              <Heading as="h4" fontSize="md" fontWeight="medium" fontFamily="body">
                {t('gfr.recorded_results')}
              </Heading>
              <Box display="flex" gap="2">
                {gfr.id === inUseGfr?.id && <Badge colorPalette="green">{t('gfr.in_use')}</Badge>}
                <Badge>{formatDateKey(gfr.labTime)}</Badge>
              </Box>
            </Box>
            <GFRAlert gfrValue={gfr.gfr} />
            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={'2'}>
              <Heading as="h4" fontSize="md" fontWeight="medium" fontFamily="body">
                {t('gfr.input_data')}
              </Heading>
              <Badge colorPalette="red">{t(`gfr.${gfr.method}`)}</Badge>
            </Box>
            <DataSection>
              <DataList items={items} />
            </DataSection>
          </Tabs.Content>
          <Tabs.Content value="activity">
            <ResourceDrawerActivitiesSection activities={activities} />
          </Tabs.Content>
        </Tabs.Root>
      </DrawerBody>

      {canEditPatient && (
        <DrawerFooter justifyContent="flex-end">
          <DeleteResourceButton variant="outline" onClick={() => void onDelete()}>
            {t('gfr.delete')}
          </DeleteResourceButton>
        </DrawerFooter>
      )}
    </>
  )
}
